import React, { useEffect, useRef } from 'react';
import { translate } from '../../Services/translation';
import { getFocusableElements } from './configuratorFocus';

const ConfiguratorPopUp = ({
  isOpen,
  onConfirm,
  onCancel,
  onClose,
  title,
  text,
  confirmText,
  cancelText,
  resetText
}) => {
  const innerRef = useRef(null);
  const confirmRef = useRef(null);
  const close = onClose || onCancel;

  useEffect(() => {
    if (!isOpen) return undefined;

    // Return focus to whatever opened the popup (typically the edit button) once it closes.
    const previouslyFocused = document.activeElement;
    confirmRef.current?.focus();

    return () => {
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [isOpen]);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    // Keep keyboard focus inside the popup while it is open.
    const focusable = getFocusableElements(innerRef.current);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="popup">
      <div
        className="popup__inner"
        ref={innerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="configurator-popup-title"
        onKeyDown={handleKeyDown}
      >
        <button
          type="button"
          onClick={(e) => {
              e.preventDefault();
              close();
            }}
          className="popup__close-icon"
          aria-label={translate('brenderup.popup.close')}
        />
        <p className="popup__title" id="configurator-popup-title">{title}</p>
        {text !== null &&
          <p className="popup__text">{text}</p>
        }
        <div className="popup__actions">
          <button
            type="button"
            ref={confirmRef}
            onClick={onConfirm}
            className="popup__confirm-button"
          >
            {confirmText}
          </button>
          {resetText &&
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onCancel();
              }}
              className="popup__reset-button"
            >
              {resetText}
            </button>
          }
          {cancelText &&
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onCancel();
              }}
              className="popup__cancel-button"
            >
              {cancelText}
            </button>
          }
        </div>
      </div>
    </div>
  );
};

export default ConfiguratorPopUp;
